import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as api from "./electricalApi";
import ElectricalAssess from "./ElectricalAssess";
import ElectricalWriteUp from "./ElectricalWriteUp";
import ElectricalExtras from "./ElectricalExtras";
import ElectricalSubmit from "./ElectricalSubmit";
import ElectricalCameraRoll from "./ElectricalPhotos";
import ElectricalReport from "./ElectricalReport";
import ElectricalNotePad from "./ElectricalNotePad";
import { ElectricalSyncBadge, ElectricalOfflineButton } from "./ElectricalOffline";
import { CarryOffer, CarryBanner } from "./ElectricalCarry";
import "./ElectricalCapture.css";

/**
 * The Electrical audit screen — route /electrical-audit/:auditId. Modeled on
 * Csa/CsaAudit.js.
 *
 * One container holds the audit, because the two passes are two views of the
 * same work: mark on site, write up at the desk. Splitting them across routes
 * would reload the whole checklist on every switch, which is exactly the
 * wrong thing on a tablet with no signal.
 *
 * Everything reaches the server through electricalApi. Nothing here calls
 * axios, so the offline layer stays swappable without this file changing.
 *
 * Electrical's checklist is area > category > checkpoint - one level
 * shallower than CSA's pillar > sub_pillar > element > question - and
 * category names recur across different areas (e.g. "TR-Signage"), so
 * everything keyed by category below uses the (area, category) pair, never
 * the category name alone. See ElectricalAssess.js's header for the same
 * note.
 */

const TABS = [
  { key: "assess", label: "Assessment" },
  { key: "writeup", label: "Write-up" },
  { key: "photos", label: "Photos" },
  { key: "extras", label: "Other checkpoints" },
  // Report comes after Finish because it does not exist until submit freezes
  // the score — the tab order is the order of the work.
  { key: "submit", label: "Finish" },
  { key: "report", label: "Report" },
];

export default function ElectricalAudit() {
  const { auditId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [framework, setFramework] = useState(null);
  // getAudit's own checkpoint list - this audit may be scoped to only some of
  // the framework's areas/categories (chosen on the new-audit form), so the
  // full framework is filtered down to this set before anything renders it.
  const [checkpointsChk, setCheckpointsChk] = useState(null);
  const [audit, setAudit] = useState(null);
  const [answers, setAnswers] = useState({});
  const [exclusions, setExclusions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [client, setClient] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canReopen, setCanReopen] = useState(false);
  const [photos, setPhotos] = useState([]);

  const [tab, setTab] = useState("assess");
  const [padOpen, setPadOpen] = useState(false);
  const [unusedNotes, setUnusedNotes] = useState(0);
  const [active, setActive] = useState(null); // { area, category }
  const [status, setStatus] = useState(null);

  /* carry-forward */
  const [carryOffer, setCarryOffer] = useState(null);
  const [carryBusy, setCarryBusy] = useState(false);
  const [previous, setPrevious] = useState(null);

  const rollupTimer = useRef(null);
  const statusTimer = useRef(null);

  /* ---------------- load ---------------- */

  useEffect(() => {
    let live = true;
    setLoading(true);
    Promise.all([api.getFramework(), api.getAudit(auditId)])
      .then(([fw, data]) => {
        if (!live) return;
        setFramework(fw);
        setCheckpointsChk(data?.checkpoints_chk || null);
        setAudit(data?.audit);
        setCanReview(!!data?.can_review);
        setCanReopen(!!data?.can_reopen);
        setClient(data?.organization);
        setAnswers(indexBy(data?.answers));
        setExclusions(data?.exclusions || []);
        setSummary(data?.summary || null);
        setLoadError(null);
      })
      .catch((e) => live && setLoadError(e.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [auditId]);

  const loadPrevious = useCallback(
    () =>
      api
        .previousAnswers(auditId)
        .then((r) => setPrevious((r && r.answers) || null))
        .catch(() => setPrevious(null)),
    [auditId]
  );

  useEffect(() => {
    if (!audit) return;
    if (audit.prior_audit_id) {
      loadPrevious();
      return;
    }
    // Never offer to carry into an audit that is finished, or one someone has
    // already started answering.
    if (audit.status !== "draft") return;
    let live = true;
    api
      .carryPreview(auditId)
      .then((p) => live && p && p.available && setCarryOffer(p))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [audit, auditId, loadPrevious]);

  const loadPhotos = useCallback(
    () => api.listPhotos(auditId).then(setPhotos).catch(() => setPhotos([])),
    [auditId]
  );

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  useEffect(() => {
    api
      .listNotes(auditId)
      .then((rows) => setUnusedNotes((rows || []).filter((n) => !n.used_for).length))
      .catch(() => {});
  }, [auditId]);

  useEffect(
    () => () => {
      clearTimeout(rollupTimer.current);
      clearTimeout(statusTimer.current);
    },
    []
  );

  /** Re-read the audit after something changed its status. */
  const reload = useCallback(
    () =>
      api
        .getAudit(auditId)
        .then((d) => {
          setAudit(d.audit);
          setCheckpointsChk(d?.checkpoints_chk || null);
          setCanReview(!!d.can_review);
          setCanReopen(!!d.can_reopen);
          setAnswers(indexBy(d.answers));
          setExclusions(d.exclusions || []);
          setSummary(d.summary || null);
        })
        .catch(() => {}),
    [auditId]
  );

  /* ---------------- derived ---------------- */

  const readOnly = !audit || audit.status !== "draft";

  /**
   * This audit's own checkpoint set, from getAudit's `checkpoints_chk`
   * (an audit can be scoped to only some areas/categories, chosen on the
   * new-audit form). Falls back to null - meaning "no scoping info, show the
   * full framework" - rather than an empty Set, so a missing or
   * differently-shaped field never silently blanks the whole screen.
   */
  const scopedCodes = useMemo(() => {
    if (!checkpointsChk || !checkpointsChk.length) return null;
    const codes = checkpointsChk
      .map((c) => String(c.checkpoint_code ?? c.code ?? "").trim())
      .filter(Boolean);
    return codes.length ? new Set(codes) : null;
  }, [checkpointsChk]);

  /**
   * The framework, filtered down to this audit's own checkpoints. Every
   * screen below reads this instead of the raw `framework` state - categories
   * and areas left with nothing in scope are dropped entirely rather than
   * shown empty.
   */
  const scopedFramework = useMemo(() => {
    if (!framework) return framework;
    if (!scopedCodes) return framework;

    const areas = framework.areas
      .map((a) => {
        const categories = a.categories
          .map((cat) => ({
            ...cat,
            checkpoints: cat.checkpoints.filter((cp) => scopedCodes.has(cp.code)),
          }))
          .filter((cat) => cat.checkpoints.length);
        return { ...a, categories };
      })
      .filter((a) => a.categories.length);

    return { ...framework, areas };
  }, [framework, scopedCodes]);

  const excludedAreas = useMemo(() => {
    const m = {};
    exclusions.forEach((e) => {
      if (e.level === "area") m[e.level_value] = e;
    });
    return m;
  }, [exclusions]);

  /** (area, category) composite key, since category names recur across areas. */
  const catKey = (area, category) => area + " " + category;

  const excludedCategories = useMemo(() => {
    const m = {};
    exclusions.forEach((e) => {
      if (e.level === "category") m[catKey(e.area, e.level_value)] = e;
    });
    return m;
  }, [exclusions]);

  const categoryNames = useMemo(() => {
    if (!scopedFramework) return [];
    const out = new Set();
    scopedFramework.areas?.forEach((a) => a.categories?.forEach((cat) => out.add(cat.category)));
    return [...out];
  }, [scopedFramework]);

  /*
   * Answered counts come from local state, not the server. They change on
   * every tap, and a round trip per tap would make the rail lag behind the
   * auditor's thumb. Conformance percentages still come from the server.
   */
  const counts = useMemo(() => {
    const byArea = {};
    const byCategory = {};
    if (!scopedFramework) return { byArea, byCategory };
    scopedFramework.areas?.forEach((a) => {
      byArea[a.area] = { done: 0, total: 0 };
      a.categories?.forEach((cat) => {
        let done = 0;
        let total = 0;
        cat.checkpoints?.forEach((cp) => {
          total += 1;
          if (answers[cp.code] && answers[cp.code].status) done += 1;
        });
        byCategory[catKey(a.area, cat.category)] = { done, total };
        byArea[a.area].done += done;
        byArea[a.area].total += total;
      });
    });
    return { byArea, byCategory };
  }, [scopedFramework, answers]);

  const carriedOpen = useMemo(() => {
    let open = 0;
    let total = 0;
    Object.values(answers).forEach((a) => {
      if (!a || !a.carried_from_audit_id) return;
      total += 1;
      if (!a.is_confirmed) open += 1;
    });
    return { open, total };
  }, [answers]);

  const totals = useMemo(() => {
    let done = 0;
    let total = 0;
    Object.values(counts.byArea).forEach((c) => {
      done += c.done;
      total += c.total;
    });

    // Scoped-out checkpoints are answered but not assessed — they leave both
    // sides of the score, so the header must not count them as progress.
    const excluded = Object.values(answers).filter((a) => a && a.na_source).length;

    return { done: done - excluded, excluded, inScope: total - excluded, total };
  }, [counts, answers]);

  /*
   * Overall conformance and its band, computed on screen rather than read
   * from summary.overall - same reasoning as ElectricalAssess.js's own
   * per-category conformance: the server rollup was never confirmed to
   * carry a usable percentage for Electrical, and this updates the instant
   * a status is tapped rather than waiting on scheduleRollup's round trip.
   *
   * Same value scheme as the assessment screen: Yes = 1, Partial = 0.5,
   * No = 0, N/A excluded entirely (not a 0 - it never joins the
   * denominator, whether the auditor marked it directly or it was scoped
   * out at category/area level - either way `status` reads "NA").
   */
  const overall = useMemo(() => {
    const STATUS_VALUE = { YES: 1, PARTIAL: 0.5, NO: 0 };
    let sum = 0;
    let count = 0;
    Object.values(answers).forEach((a) => {
      if (!a || !a.status) return;
      const key = String(a.status).toUpperCase();
      if (key === "NA" || !(key in STATUS_VALUE)) return;
      sum += STATUS_VALUE[key];
      count += 1;
    });
    if (!count) return null;
    const pct = Math.round((sum / count) * 100);
    const band =
      pct >= 85 ? "Optimised" :
      pct >= 70 ? "Established" :
      pct >= 50 ? "Developing" :
      pct > 0 ? "Initial" : "Not started";
    return { pct, band };
  }, [answers]);

  // Land on the first category that still has work, not always on area 1.
  useEffect(() => {
    if (active || !scopedFramework) return;
    for (const a of scopedFramework.areas || []) {
      for (const cat of a.categories || []) {
        const c = counts.byCategory[catKey(a.area, cat.category)];
        if (c && c.done < c.total) {
          setActive({ area: a.area, category: cat.category });
          return;
        }
      }
    }
    const firstArea = scopedFramework.areas?.[0];
    const firstCat = firstArea?.categories?.[0];
    if (firstArea && firstCat) setActive({ area: firstArea.area, category: firstCat.category });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedFramework]);

  /** Take the auditor to the first carried answer nobody has looked at. */
  const jumpToFirstCarry = useCallback(() => {
    if (!scopedFramework) return;
    for (const a of scopedFramework.areas || []) {
      for (const cat of a.categories || []) {
        for (const cp of cat.checkpoints || []) {
          const ans = answers[cp.code];
          if (ans && ans.carried_from_audit_id && !ans.is_confirmed) {
            setActive({ area: a.area, category: cat.category });
            setTab("assess");
            setTimeout(() => {
              const node = document.getElementById("q-" + cp.code);
              if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
            return;
          }
        }
      }
    }
  }, [scopedFramework, answers]);

  /* ---------------- status line ---------------- */

  const flash = useCallback((kind, text, ms) => {
    clearTimeout(statusTimer.current);
    setStatus({ kind, text });
    if (ms) statusTimer.current = setTimeout(() => setStatus(null), ms);
  }, []);

  /** Scores are the server's business — ask it, but not on every keystroke. */
  const scheduleRollup = useCallback(() => {
    clearTimeout(rollupTimer.current);
    rollupTimer.current = setTimeout(() => {
      api
        .getRollup(auditId)
        .then(setSummary)
        .catch(() => {});
    }, 2500);
  }, [auditId]);

  /* ---------------- writes ---------------- */

  const write = useCallback(
    async (code, patch) => {
      // console.log('-- write',code,' patch:',patch);
      const before = answers[code];
      if (!before) return;

      // Optimistic: the button must colour under the thumb, not after a
      // round trip. A failure below puts the old value back.
      setAnswers((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));
      flash("saving", "Saving…");

      try {
        const row = await api.saveAnswer(auditId, {
          checkpoint_code: code,
          row_version: before.row_version,
          ...patch,
        });
        setAnswers((prev) => ({ ...prev, [code]: row }));
        flash("saved", "Saved", 1200);
        scheduleRollup();
      } catch (e) {
        if (e.isConflict && e.body && e.body.current) {
          // Someone else already changed this. Show theirs rather than
          // overwrite it — never a silent last-writer-wins.
          setAnswers((prev) => ({
            ...prev,
            [code]: { ...prev[code], ...e.body.current },
          }));
          flash("error", `${code} was changed elsewhere — showing the saved version`, 6000);
        } else {
          setAnswers((prev) => ({ ...prev, [code]: before }));
          flash("error", e.message, 6000);
        }
      }
    },
    [answers, auditId, flash, scheduleRollup]
  );

  const onAnswer = useCallback(
    // console.log('-- onAnswer',value)
    // `status` is the real backend column (elec_answer.status). Sending
    // `answer` instead - CSA's field name, not Electrical's - means the
    // status never actually persists server-side: the button looks picked
    // on screen, but the saved row has no status, so anything that asks the
    // server "is this answered" (the Finish tab's readiness check included)
    // correctly reports it as unanswered.
    (code, value) => write(code, { status: value, set_by_bulk: false }),
    [write]
  );

  const onObservation = useCallback(
    (code, text) => write(code, { observation: text }),
    [write]
  );

  const onRecommendation = useCallback(
    (code, text) => write(code, { recommendation: text }),
    [write]
  );

  /** Checkpoint codes under one area or (area, category) scope. */
  const codesInScope = useCallback(
    (level, levelValue, areaValue) => {
      const codes = [];
      (scopedFramework?.areas || []).forEach((a) => {
        if (level === "area" && a.area !== levelValue) return;
        if (level === "category" && a.area !== areaValue) return;
        (a.categories || []).forEach((cat) => {
          if (level === "category" && cat.category !== levelValue) return;
          (cat.checkpoints || []).forEach((cp) => codes.push(cp.code));
        });
      });
      return codes;
    },
    [scopedFramework]
  );

  /** Answer by exception — fill the blanks in one area or category with a compliant status. */
  const onBulkCompliant = useCallback(
    async (level, levelValue, areaValue) => {
      const codes = codesInScope(level, levelValue, areaValue);
      if (!codes.length) return;
      flash("saving", "Setting the remaining checkpoints…");
      try {
        const r = await api.bulkMarkCompliant(auditId, level, levelValue, undefined, areaValue);
        // Applied locally rather than re-fetched — same rule as the server:
        // every row still blank becomes compliant.
        setAnswers((prev) => {
          const next = { ...prev };
          codes.forEach((code) => {
            const cur = next[code];
            if (cur && !cur.status) {
              next[code] = {
                ...cur,
                status: "YES",
                set_by_bulk: true,
                row_version: cur.row_version + 1,
              };
            }
          });
          return next;
        });
        flash("saved", `${r.answered} set compliant · ${r.left_untouched} left as answered`, 3000);
        scheduleRollup();
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [codesInScope, auditId, flash, scheduleRollup]
  );

  /* ---------------- carry-forward ---------------- */

  const onCarry = useCallback(async () => {
    setCarryBusy(true);
    try {
      const r = await api.carryForward(auditId);
      // Re-read rather than patch locally — carrying rewrites every row and
      // its row_version; guessing at them here would hand the next edit a
      // stale version and a 409 the auditor did nothing to deserve.
      const data = await api.getAudit(auditId);
      setAudit(data.audit);
      setAnswers(indexBy(data.answers));
      setExclusions(data.exclusions || []);
      setSummary(data.summary || null);
      setCarryOffer(null);
      await loadPrevious();
      flash(
        "saved",
        `${r.carried} answers carried across. All ${r.needs_confirming} still need checking.`,
        6000
      );
    } catch (e) {
      flash("error", e.message, 6000);
    } finally {
      setCarryBusy(false);
    }
  }, [auditId, flash, loadPrevious]);

  const onStartFresh = useCallback(() => {
    setCarryOffer(null);
    flash("saved", "Starting fresh. The previous visit is still on file.", 3500);
  }, [flash]);

  const confirmCodes = useCallback(
    async (body, describe) => {
      setCarryBusy(true);
      try {
        const r = await api.confirmCarried(auditId, body);
        const data = await api.getAudit(auditId);
        setAnswers(indexBy(data.answers));
        flash("saved", describe(r), 3500);
      } catch (e) {
        flash("error", e.message, 6000);
      } finally {
        setCarryBusy(false);
      }
    },
    [auditId, flash]
  );

  const onConfirm = useCallback(
    (code) =>
      confirmCodes({ checkpoint_codes: [code] }, (r) =>
        r.still_unconfirmed
          ? `Checked. ${r.still_unconfirmed} left.`
          : "Checked — that was the last one."
      ),
    [confirmCodes]
  );

  const onConfirmAll = useCallback(
    () =>
      confirmCodes({ all: true }, (r) => `${r.confirmed} confirmed as they stand.`),
    [confirmCodes]
  );

  /* ---------------- scope ---------------- */

  const onExclude = useCallback(
    async (level, levelValue, justification, areaValue) => {
      try {
        const r = await api.addExclusion(auditId, {
          level,
          level_value: levelValue,
          justification,
          area: areaValue,
        });
        setExclusions((prev) => [...prev, r.exclusion]);
        const data = await api.getAudit(auditId);
        const freshAnswers = indexBy(data.answers);
        setAnswers(freshAnswers);
        setSummary(data.summary || null);
        flash(
          "saved",
          r.left_untouched.length
            ? `${r.marked_na} marked N/A · ${r.left_untouched.length} kept their answer`
            : `${r.marked_na} marked N/A`,
          4000
        );

        /*
         * The reason given for the whole area/category becomes each affected
         * checkpoint's own observation too - a finding should read as
         * complete on its own, not send a reader to cross-reference the
         * exclusion list to learn why it shows N/A. Only the checkpoints
         * this exclusion actually touched get it (no prior answer, same
         * rule onBulkCompliant uses to decide who it sets compliant) - one
         * that was already answered keeps what the auditor wrote there.
         *
         * Uses the freshly reloaded row_version, not the pre-exclusion one
         * still sitting in `answers` here - the exclusion just bumped it
         * server-side, and sending the stale value would read as someone
         * else's edit and 409.
         */
        if (justification) {
          const justExcluded = codesInScope(level, levelValue, areaValue).filter(
            (code) => !(answers[code] && answers[code].status)
          );
          await Promise.all(
            justExcluded.map(async (code) => {
              const fresh = freshAnswers[code];
              if (!fresh) return;
              try {
                const row = await api.saveAnswer(auditId, {
                  checkpoint_code: code,
                  row_version: fresh.row_version,
                  observation: justification,
                });
                setAnswers((prev) => ({ ...prev, [code]: row }));
              } catch (e) {
                // One checkpoint failing to take the note must not undo the
                // exclusion itself or block the rest.
              }
            })
          );
        }
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [auditId, flash, codesInScope, answers]
  );

  const onRestoreScope = useCallback(
    async (exclusionId) => {
      try {
        await api.removeExclusion(auditId, exclusionId);
        setExclusions((prev) => prev.filter((e) => e.id !== exclusionId));
        const data = await api.getAudit(auditId);
        setAnswers(indexBy(data.answers));
        setSummary(data.summary || null);
        flash("saved", "Back in scope", 2500);
      } catch (e) {
        flash("error", e.message, 6000);
      }
    },
    [auditId, flash]
  );

  /* ---------------- render ---------------- */

  if (loading) {
    return (
      <div className="elec-capture elec-screen">
        <div className="cap-loading">Loading the audit…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="elec-capture elec-screen">
        <div className="cap-loading">
          <p>{loadError}</p>
          <button type="button" className="cap-btn" onClick={() => navigate("/electrical-audits")}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="elec-capture elec-screen">
      <header className="cap-head">
        <button
          type="button"
          className="cap-back"
          title="Back to your audits"
          onClick={() => navigate("/electrical-audits")}
        >
          ‹
        </button>
        <div>
          <h1>{(client && client.org_name) || "Electrical audit"}</h1>
          <div className="sub">
            {audit?.site_name || "—"} · {audit?.report_id}
            {readOnly ? ` · ${audit?.status}` : ""}
          </div>
        </div>

        <div className="cap-spacer" />

        <div className="cap-progress">
          <span className="cap-pct">
            {!overall || overall.pct == null ? "—" : `${overall.pct}%`}
          </span>
          <span className="cap-band">{(overall && overall.band) || "Not started"}</span>
          <span className="cap-prog">
            {totals.done} assessed
            {totals.excluded ? ` · ${totals.excluded} scoped out` : ""}
            {" · "}
            {totals.inScope} in scope of {totals.total}
          </span>
        </div>

        <ElectricalSyncBadge auditId={auditId} />

        <button type="button" className="cap-btn ghost" onClick={() => setPadOpen(true)}>
          Note pad{unusedNotes ? ` · ${unusedNotes}` : ""}
        </button>

        <button type="button" className="cap-btn" onClick={() => navigate("/electrical-audits")}>
          Done for now
        </button>
      </header>

      <nav className="cap-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={"cap-tab" + (tab === t.key ? " on" : "")}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "assess" ? (
        <>
          <div className="cap-offline-slot">
            <ElectricalOfflineButton auditId={auditId} />
          </div>
          {readOnly ? null : (
            <CarryBanner
              outstanding={carriedOpen.open}
              total={carriedOpen.total}
              busy={carryBusy}
              onReview={jumpToFirstCarry}
              onConfirmAll={onConfirmAll}
            />
          )}
          <ElectricalAssess
            framework={scopedFramework}
            auditId={auditId}
            photos={photos}
            onPhotosChanged={loadPhotos}
            answers={answers}
            counts={counts}
            summary={summary}
            excludedAreas={excludedAreas}
            excludedCategories={excludedCategories}
            active={active}
            setActive={setActive}
            readOnly={readOnly}
            previous={previous}
            onConfirm={onConfirm}
            onAnswer={onAnswer}
            onObservation={onObservation}
            onRecommendation={onRecommendation}
            onBulkCompliant={onBulkCompliant}
            onExclude={onExclude}
            onRestoreScope={onRestoreScope}
          />
        </>
      ) : null}

      {tab === "writeup" ? (
        <ElectricalWriteUp
          auditId={auditId}
          framework={scopedFramework}
          readOnly={readOnly}
          onObservation={onObservation}
          onNotesChanged={setUnusedNotes}
          onJump={(code, area, category) => {
            // Called with no arguments from the empty state — that just
            // means "take me back", with no particular checkpoint to land on.
            if (area && category) setActive({ area, category });
            setTab("assess");
            if (!code) return;
            setTimeout(() => {
              const el = document.getElementById("q-" + code);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
          }}
        />
      ) : null}

      {tab === "photos" ? (
        <ElectricalCameraRoll
          auditId={auditId}
          framework={scopedFramework}
          answers={answers}
          readOnly={readOnly}
          onChange={loadPhotos}
        />
      ) : null}

      {tab === "report" ? <ElectricalReport auditId={auditId} audit={audit} /> : null}

      {tab === "extras" ? (
        <ElectricalExtras
          auditId={auditId}
          readOnly={readOnly}
          framework={scopedFramework}
          photos={photos}
          onPhotosChanged={loadPhotos}
        />
      ) : null}

      {tab === "submit" ? (
        <ElectricalSubmit
          auditId={auditId}
          audit={audit}
          canReview={canReview}
          canReopen={canReopen}
          onSubmitted={() => reload()}
          onChanged={() => reload()}
        />
      ) : null}

      {status ? <div className={"cap-status " + status.kind}>{status.text}</div> : null}

      {carryOffer ? (
        <CarryOffer
          preview={carryOffer}
          busy={carryBusy}
          onCarry={onCarry}
          onStartFresh={onStartFresh}
        />
      ) : null}

      <ElectricalNotePad
        auditId={auditId}
        open={padOpen}
        onClose={() => setPadOpen(false)}
        categories={categoryNames}
        currentCategory={tab === "assess" ? active?.category : null}
        onCountChange={setUnusedNotes}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function indexBy(rows) {
  const m = {};
  (rows || []).forEach((r) => {
    m[r.checkpoint_code] = r;
  });
  return m;
}
